import { createSelector } from 'reselect';
import moment from 'moment';
import isEmpty from 'lodash/isEmpty';

import {
  convertNumToCurrencyStr,
  formatDate,
  interpolate,
  snakeCaseKeys,
} from '../../../utils/globalHelpers';
import makeSelectMain, { getNextPaymentDueDetails } from '../../Main/selectors';
import makeSelectPaymentSources, {
  makeSelectChosenAccountInfo,
  makeSelectChosenAccountType,
  makeSelectPayFromButtonProps,
  makeSelectUseAccountInfoExistingByPaymentType,
} from '../Sources/selectors';
import { CONFIRMATION_DICTIONARY_OTP } from './constants';
import { initialState } from './reducer';

/**
 * Direct selector to the One Time Payment state domain
 */
const selectOtpDomain = (state) => state.oneTimePayment || initialState;

/**
 * Other specific selectors
 */

/**
 * @description ...
 */
const selectCancelModalProps = () => createSelector(
  makeSelectOtp('loading'),
  selectIsModalOpen('cancel'),
  selectOtp('pendingPaymentTotal'),
  selectOtp('pendingScheduledDate'),
  ({ otp: otpLoading }, isOpen, amount, date) =>
    ({
      amount: convertNumToCurrencyStr(amount),
      date: date ? formatDate(date, 'UTC', 'LL') : '',
      isOpen,
      loading: otpLoading,
    }),
);

/**
 * @description Selects the formatted request body for creating an One-Time Payment
 */
const selectCreateOtpRequestBody = () => createSelector(
  makeSelectChosenAccountType(),
  selectOtp('payType'),
  selectOtp('scheduledDate'),
  makeSelectUseAccountInfoExistingByPaymentType('otp'),
  makeSelectChosenAccountInfo('otp'),
  selectOtpForm('amount'),
  makeSelectOtp('nachaToken'),
  (
    accountType,
    payType,
    scheduledDate,
    useAccountInfoExisting,
    accountValues,
    amountValues,
    nachaToken,
  ) => {
    if (useAccountInfoExisting) {
      return snakeCaseKeys({
        payType,
        scheduledDate,
        useExistingAccountInformation: true,
        ...amountValues,
      });
    }
    return snakeCaseKeys({
      accountType,
      paymentMethodVerificationToken: nachaToken,
      payType,
      scheduledDate,
      ...accountValues,
      ...amountValues,
    });
  },
);

/**
 * @description selects data utilized by OTP edit amount drawer on desktop
 */
const selectDesktopAmountDrawerProps = () => createSelector(
  selectOtpForm('amount'),
  selectOtpFormErrors('amount'),
  selectOtp('availableNumberOfPayments'),
  getNextPaymentDueDetails(),
  selectOtpFields(),
  selectOtp('maximumAllowedAdditionalPrincipal'),
  selectOtp('maximumAllowedTotalPayment'),
  selectOtp('nsfFees'),
  selectOtp('otherFees'),
  selectOtp('outstandingLateCharges'),
  selectOtp('paymentLogic'),
  selectOtp('payType'),
  selectRegularMonthlyPayment(),
  selectTotalPayment(),
  selectOtp('isAfterGracePeriod'),
  selectIsScheduledDateAfterGracePeriod(),
  selectShowOtpHelocDisclaimer(),
  (
    amountForm,
    amountFormErrors,
    availableNumberOfPayments,
    { nextPaymentDueDate },
    fields,
    maxAdditionalPrincipal,
    maxTotalPayment,
    nsfFees,
    otherFees,
    outstandingLateCharges,
    paymentLogic,
    payType,
    regularMonthlyPayment,
    totalPayment,
    isAfterGracePeriod,
    isScheduledDateAfterGracePeriod,
    showOtpHelocDisclaimer,
  ) => ({
    amountForm,
    amountFormErrors,
    availableNumberOfPayments,
    dueDate: formatDate(nextPaymentDueDate, 'UTC', 'LL'),
    fields,
    isAfterGracePeriod,
    isScheduledDateAfterGracePeriod,
    maxEqualValues: {
      additionalPrincipal: maxAdditionalPrincipal,
      lateCharge: outstandingLateCharges,
      nsfFees,
      otherFees,
    },
    maxTotalPayment,
    paymentLogic,
    payType,
    regularMonthlyPayment,
    showOtpHelocDisclaimer,
    totalPayment,
  }),
);

/**
 * @description selects data utilized by OTP edit date drawer on desktop
 */
const selectDesktopDateDrawerProps = () => createSelector(
  selectDisableDateSelectButton(),
  selectFormattedAvailableDates(),
  selectDesktopPickerDates(),
  selectOtp('isAfterGracePeriod'),
  selectIsScheduledDateFormAfterGracePeriod(),
  selectOtp('payType'),
  (
    disableSelect,
    availableDates,
    desktopPickerDates,
    isAfterGracePeriod,
    isScheduledDateFormAfterGracePeriod,
    payType,
  ) => ({
    availableDates: Object.keys(availableDates),
    disableSelect,
    isAfterGracePeriod,
    isScheduledDateFormAfterGracePeriod,
    payType,
    pickerDates: desktopPickerDates,
  }),
);

/**
 * selects and formats date values and formats to be consumed by date picker component
 */
const selectDesktopPickerDates = () => createSelector(
  getNextPaymentDueDetails(),
  selectOtpForm('account'),
  selectOtp('scheduledDate'),
  (
    { nextPaymentDueDate },
    { scheduledDate: scheduledDateForm },
    scheduledDateOtp,
  ) => ({
    dueDate: nextPaymentDueDate ? moment(nextPaymentDueDate) : null,
    scheduledDateForm: scheduledDateForm ? moment(scheduledDateForm, 'YYYY-MM-DD') : null,
    scheduledDateOtp: scheduledDateOtp ? moment(scheduledDateOtp) : null,
  }),
);

/**
 * @description determines whether or not button should be disabled via state values
 */
const selectDisableDateSelectButton = () => createSelector(
  selectMobilePickerDates(),
  ({ scheduledDateForm, scheduledDateOtp }) =>
    !scheduledDateForm || scheduledDateForm === scheduledDateOtp,
);

/**
 * @description selects and formats language for disclosure component
 */
const selectDisclosureLanguage = () => createSelector(
  selectOtpScheduledDate(),
  selectTotalPayment(),
  (scheduledDate, totalPayment) => ({
    headerText: 'Terms & Conditions',
    summaryButtonLabel: 'Pay Now',
    summarySubText: `Scheduled for ${scheduledDate}`,
    summaryText: totalPayment,
  }),
);

/**
 * @description selects and formats available dates to be consumed by calendar component
 */
const selectFormattedAvailableDates = () => createSelector(
  selectOtp('availableDraftDates'),
  (availableDraftDates) => availableDraftDates.reduce((acc, date) => {
    acc[date.split('T')[0]] = { disabled: false };
    return acc;
  }, {}),
);

const selectFormattedBannerError = () => createSelector(
  selectOtpError('bannerError'),
  selectOtp('maximumAllowedTotalPayment'),
  (bannerError, maxTotalPayment) => {
    if (bannerError) {
      if (bannerError.includes('{{maxTotalPayment}}')) {
        return interpolate(
          bannerError,
          { maxTotalPayment: convertNumToCurrencyStr(maxTotalPayment) },
        );
      }
    }
    return bannerError;
  },
);

const selectIsAllowedToScheduleOTP = () => createSelector(
  selectOtp('allowedOperation'),
  (allowedOperation) => allowedOperation === 'Schedule_Payment',
);

const selectIsScheduledDateAfterGracePeriod = () => createSelector(
  selectOtp('dateAfterGracePeriod'),
  selectOtp('scheduledDate'),
  (
    dateAfterGracePeriod,
    scheduledDate,
  ) => (moment(scheduledDate).isSameOrAfter(moment(dateAfterGracePeriod), 'day')),
);

const selectIsScheduledDateFormAfterGracePeriod = () => createSelector(
  selectOtp('dateAfterGracePeriod'),
  selectOtpForm('account'),
  (
    dateAfterGracePeriod,
    { scheduledDate: scheduledDateFormValue },
  ) => (moment(scheduledDateFormValue).isSameOrAfter(moment(dateAfterGracePeriod), 'day')),
);

const selectShouldOTPReviewShowLatePaymentWarning = () => createSelector(
  selectOtp('isAfterGracePeriod'),
  selectIsScheduledDateAfterGracePeriod(),
  selectOtp('payType'),
  (
    isAfterGracePeriod,
    isScheduledDateAfterGracePeriod,
    payType,
  ) => (
    payType === 'Monthly'
    && !isAfterGracePeriod
    && isScheduledDateAfterGracePeriod
  ),
);

const selectIsModalOpen = (prop) => createSelector(
  selectOtp('isModalOpen'),
  (isModalOpenObject) => isModalOpenObject[prop],
);

/**
 * selects and formats date values and formats to be consumed by date picker component
 */
const selectMobilePickerDates = () => createSelector(
  getNextPaymentDueDetails(),
  selectOtpForm('account'),
  selectOtp('scheduledDate'),
  (
    { nextPaymentDueDate },
    { scheduledDate: scheduledDateForm },
    scheduledDateOtp,
  ) => ({
    dueDate: nextPaymentDueDate.split('T')[0],
    scheduledDateForm: scheduledDateForm.split('T')[0],
    scheduledDateOtp: scheduledDateOtp.split('T')[0],
  }),
);

/**
 * @description Selects One Time Payment form inputs depending on prop
 */
const selectOtp = (prop) => createSelector(
  makeSelectOtp('otp'),
  (otp) => otp[prop],
);

/**
 * @description Selects otp cancel confirmation data, returns object or false
 */
const selectOtpCancelConfirmation = () => createSelector(
  selectOtp('cancelConfirmation'),
  (cancelConfirmationData) => {
    if (cancelConfirmationData) {
      const {
        amount,
        confirmationNumber = '',
        date,
        status,
      } = cancelConfirmationData;
      return {
        amount: convertNumToCurrencyStr(amount),
        confirmationNumber,
        date: formatDate(date, 'UTC', 'LL'),
        status,
      };
    }
    return cancelConfirmationData;
  },
);

/**
 * @description Selects otp confirmation, returns either false or array
 */
const selectOtpConfirmation = () => createSelector(
  selectOtp('confirmation'),
  (confirmation) => {
    if (confirmation) {
      const { labelArray } = CONFIRMATION_DICTIONARY_OTP;
      return labelArray.map(({ key, label }) => {
        const obj = { label, value: confirmation[key] };
        if (key === 'scheduledTotalPayment') {
          obj.value = convertNumToCurrencyStr(obj.value);
        }
        if (key === 'scheduledDate') {
          obj.value = formatDate(obj.value, 'UTC', 'LL');
        }
        if (key === 'numberOfPayments') {
          obj.value = obj.value.toString();
        }
        return obj;
      });
    }
    return confirmation;
  },
);

/**
 * @description Selects if loan has late charges, returns boolean
 */
const selectHasLateCharges = () => createSelector(
  selectOtp('outstandingLateCharges'),
  (lateCharges) => lateCharges > 0,
);

/**
 * @description selects and formats due date
 */
const selectOtpDueDate = () => createSelector(
  getNextPaymentDueDetails(),
  ({ nextPaymentDueDate }) => nextPaymentDueDate ? formatDate(nextPaymentDueDate, 'UTC', 'LL') : '',
);

/**
 * @description Selects a category of error from the error object
 */
const selectOtpError = (prop) => createSelector(
  makeSelectOtp('errors'),
  (errorState) => errorState[prop],
);

const selectOtpFields = () => createSelector(
  selectOtp('fields'),
  selectOtp('nsfFees'),
  selectOtp('otherFees'),
  selectOtp('outstandingLateCharges'),
  (
    fields,
    nsfFees,
    otherFees,
    outstandingLateCharges,
  ) => (
    fields.map((obj) => {
      const { enabled, field } = obj;
      if (field === 'lateCharge' && enabled) {
        const labelSubText = `${convertNumToCurrencyStr(outstandingLateCharges)} is due`;
        return {
          ...obj,
          labelSubText,
        };
      }
      if (field === 'nsfFees' && enabled) {
        const labelSubText = `${convertNumToCurrencyStr(nsfFees)} is due`;
        return {
          ...obj,
          labelSubText,
        };
      }
      if (field === 'otherFees' && enabled) {
        const labelSubText = `${convertNumToCurrencyStr(otherFees)} is due`;
        return {
          ...obj,
          labelSubText,
        };
      }
      return obj;
    })
  ),
);

/**
 * @description Selects otp form
 */
const selectOtpForm = (prop) => createSelector(
  selectOtp('forms'),
  (forms) => forms[prop],
);

/**
 * @description Creates a selector that grabs specific form errors from the otp object
 * @param {string} prop Desired prop from the otp form errors object
 */
const selectOtpFormErrors = (prop) => createSelector(
  selectOtp('formErrors'),
  (formErrors) => formErrors[prop],
);

/**
 * @description Selects a specified loading state for One Time Payment based on the prop
 * @param {string} prop Desired loading state
 */
const selectOtpLoading = (prop) => createSelector(
  makeSelectOtp('loading'),
  (loadingState) => loadingState[prop],
);

/**
 * @description Selects a specified request status for One Time Payment based on the prop
 * @param {string} prop Desired loading state
 */
const selectOtpRequestStatus = (prop) => createSelector(
  makeSelectOtp('requestStatus'),
  (requestStatus) => requestStatus[prop],
);

/**
 * @description creates error notifications array to be consumed by screen reader
 */
const selectOtpNotifications = () => createSelector(
  selectOtpError('bannerError'),
  selectOtpError('serviceError'),
  makeSelectPaymentSources('validateRoutingError'),
  (
    bannerError,
    {
      createOtp: createOtpError,
      otp: otpError,
      paymentHome: paymentHomeError,
    },
    validateRoutingError,
  ) => {
    const errorPrecedenceArray = [
      otpError,
      createOtpError,
      paymentHomeError,
      validateRoutingError,
      bannerError,
    ];
    return errorPrecedenceArray.filter((errorString) => !!errorString.length);
  },
);

/**
 * @description selects values for otp review ui and formats as object array
 */
const selectOtpReviewData = () => createSelector(
  selectTotalPayment(),
  selectOtpForm('amount'),
  makeSelectPayFromButtonProps('otp'),
  selectOtpScheduledDate(),
  (
    totalPayment,
    { numberOfPayments },
    { value: paymentSource },
    scheduledDate,
  ) => [
    { label: 'Payment Amount', value: totalPayment },
    { label: 'Number of Payments', value: `${numberOfPayments}` },
    { label: 'Payment Source', value: paymentSource },
    { label: 'Scheduled Date', value: scheduledDate },
  ],
);

/**
 * selects and formats scheduled date
 */
const selectOtpScheduledDate = () => createSelector(
  selectOtp('scheduledDate'),
  (scheduledDate) => scheduledDate ? formatDate(scheduledDate, 'UTC', 'LL') : '',
);

/**
 * selects and formats scheduled date with abbreviated month
 */
const selectOtpScheduledDateShort = () => createSelector(
  selectOtp('scheduledDate'),
  (scheduledDate) => scheduledDate ? formatDate(scheduledDate, 'UTC', 'll') : '',
);

const selectOtpServiceError = (prop) => createSelector(
  selectOtpError('serviceError'),
  (serviceError) => serviceError[prop],
);

const selectPaymentHomeData = () => createSelector(
  getNextPaymentDueDetails(),
  selectOtp('pendingScheduledDate'),
  selectOtp('pendingPaymentTotal'),
  selectOtpDueDate(),
  (
    nextPaymentDetails,
    pendingScheduledDate,
    pendingPaymentTotal,
  ) => {
    if (!isEmpty(nextPaymentDetails)) {
      const { nextPaymentDueDate, regularMonthlyPayment } = nextPaymentDetails;
      const dueDateMessage = nextPaymentDueDate ?
        `Due on ${formatDate(nextPaymentDueDate, 'UTC', 'MMMM D, YYYY')}` :
        'No Payment Due';
      return {
        dueDateMessage,
        monthlyAmountDue: convertNumToCurrencyStr(regularMonthlyPayment),
        pendingPaymentTotal: convertNumToCurrencyStr(pendingPaymentTotal),
        pendingScheduledDate: pendingScheduledDate ?
          formatDate(pendingScheduledDate, 'UTC', 'MMMM Do') : '',
      };
    }
    return {};
  },
);

/**
 * @description Selects regular monthly payment from One Time Payment state and
 *              formats it from number to currency string
 * @return {string} Formatted regular monthly payment
 */
const selectRegularMonthlyPayment = () => createSelector(
  getNextPaymentDueDetails(),
  ({ regularMonthlyPayment }) => convertNumToCurrencyStr(regularMonthlyPayment),
);

/** @description selects boolean to display HELOC disclaimer text */
const selectShowOtpHelocDisclaimer = () => createSelector(
  makeSelectMain('loanDetails'),
  ({ hasEloc, hasSegmentedEloc }) => hasEloc || hasSegmentedEloc,
);

/**
 * @description Selects appropriate step
 */
const selectStep = (prop) => createSelector(
  makeSelectOtp('steps'),
  (steps) => steps[prop],
);

/**
 * @description Calculates and formats the total payment
 * @return {string} Formatted total payment
 */
const selectTotalPayment = () => createSelector(
  selectOtpForm('amount'),
  selectOtp('payType'),
  getNextPaymentDueDetails(),
  (amount, payType, { regularMonthlyPayment }) => {
    const {
      additionalEscrow,
      additionalPrincipal,
      lateCharge,
      nsfFees,
      numberOfPayments,
      otherFees,
    } = amount;
    const monthlyTotal = payType === 'Monthly' ? regularMonthlyPayment * numberOfPayments : 0;
    const additionalTotal = additionalEscrow + additionalPrincipal +
      lateCharge + otherFees + nsfFees;
    return convertNumToCurrencyStr(monthlyTotal + additionalTotal);
  },
);

/**
 * @description selects maximum number of delay days allowed
 */
const selectMaxDelayDays = () => createSelector(
  makeSelectMain('loanDetails'),
  ({ payment }) => {
    const { gracePeriod } = payment || {};
    if (gracePeriod) {
      return gracePeriod;
    }
    return 0;
  },
);

/**
 * Default selector used by One Time Payment
 */
const makeSelectOtp = (prop) => createSelector(
  selectOtpDomain,
  (state) => state[prop],
);

export default makeSelectOtp;
export {
  selectCancelModalProps,
  selectCreateOtpRequestBody,
  selectDesktopAmountDrawerProps,
  selectDesktopDateDrawerProps,
  selectDisableDateSelectButton,
  selectDisclosureLanguage,
  selectFormattedAvailableDates,
  selectFormattedBannerError,
  selectHasLateCharges,
  selectIsAllowedToScheduleOTP,
  selectIsModalOpen,
  selectIsScheduledDateAfterGracePeriod,
  selectIsScheduledDateFormAfterGracePeriod,
  selectMaxDelayDays,
  selectMobilePickerDates,
  selectOtp,
  selectOtpCancelConfirmation,
  selectOtpConfirmation,
  selectOtpDueDate,
  selectOtpError,
  selectOtpFields,
  selectOtpForm,
  selectOtpFormErrors,
  selectOtpLoading,
  selectOtpNotifications,
  selectOtpRequestStatus,
  selectOtpReviewData,
  selectOtpScheduledDate,
  selectOtpScheduledDateShort,
  selectOtpServiceError,
  selectPaymentHomeData,
  selectRegularMonthlyPayment,
  selectShouldOTPReviewShowLatePaymentWarning,
  selectShowOtpHelocDisclaimer,
  selectStep,
  selectTotalPayment,
};
